import { useState } from 'react';
import type React from 'react';

export interface ListboxKeys {
  readonly activeIndex: number;
  readonly setActiveIndex: (index: number) => void;
  readonly onKeyDown: (e: React.KeyboardEvent) => void;
}

export interface ListboxKeysInput {
  readonly itemCount: number;
  readonly open: boolean;
  readonly openMenu: () => void;
  readonly closeMenu: () => void;
  readonly onCommit: (index: number) => void;
  /** Leave false where the trigger is a text input and Space types. */
  readonly spaceCommits?: boolean;
  /** Where a fresh open puts the cursor. Point it at the selected row on a
   *  single-select, so the first arrow key moves from the current value. */
  readonly initialIndex?: number;
}

/** Arrow / Enter / Space / Escape for a portaled listbox, shared so the single-
 *  and multi-select fields answer the keyboard alike. */
export function useListboxKeys({
  itemCount,
  open,
  openMenu,
  closeMenu,
  onCommit,
  spaceCommits = false,
  initialIndex = 0,
}: ListboxKeysInput): ListboxKeys {
  // Stored against the open state it belongs to, so a reopen cannot inherit the
  // previous highlight without an effect to clear it.
  const [pick, setPick] = useState({ forOpen: open, index: initialIndex });
  if (pick.forOpen !== open) {
    setPick({ forOpen: open, index: initialIndex });
  }
  const activeIndex = Math.min(pick.index, Math.max(0, itemCount - 1));
  const setActiveIndex = (index: number) => setPick({ forOpen: open, index });

  const onKeyDown = (e: React.KeyboardEvent) => {
    const commits = e.key === 'Enter' || (spaceCommits && e.key === ' ');
    if (!open) {
      if (e.key === 'ArrowDown' || commits) {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(Math.min(activeIndex + 1, itemCount - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(Math.max(activeIndex - 1, 0));
    } else if (commits) {
      e.preventDefault();
      onCommit(activeIndex);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeMenu();
    }
  };

  return { activeIndex, setActiveIndex, onKeyDown };
}
